import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { FaEdit, FaEllipsisV, FaExternalLinkAlt, FaTrash } from "react-icons/fa";

const MENU_MIN_WIDTH = 200;
const MENU_GAP = 8;

const triggerClass = (open) =>
  [
    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white text-slate-500 shadow-sm outline-none transition",
    "hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700",
    "focus-visible:ring-2 focus-visible:ring-[#007BFF]/25 focus-visible:ring-offset-1",
    open
      ? "border-slate-300 bg-slate-50 text-slate-700 ring-2 ring-[#007BFF]/15"
      : "",
  ].join(" ");

const menuItemClass = (danger, disabled) => {
  if (disabled) {
    return "group flex w-full cursor-not-allowed items-center gap-2.5 rounded-md px-2 py-2 text-left opacity-45";
  }
  if (danger) {
    return "group flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-[13px] font-medium text-red-600 transition-colors hover:bg-red-50/90 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200/80";
  }
  return "group flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-100/90 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200/80";
};

const iconWrapClass = (danger) =>
  danger
    ? "flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-500 transition group-hover:bg-red-100/80 group-hover:text-red-600"
    : "flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500 transition group-hover:bg-slate-200/80 group-hover:text-slate-700";

export function CompanyFormsManageActions({
  version,
  canEditForm,
  canDeleteVersion,
  submitting,
  onOpenFile,
  onEditForm,
  onDeleteVersion,
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ visibility: "hidden" });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const showMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const hideMenu = useCallback(() => {
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  }, []);

  const { primaryItems, dangerItems } = useMemo(() => {
    const primary = [];
    const danger = [];
    if (version?.file_url) {
      primary.push({
        key: "open",
        label: "Open file",
        Icon: FaExternalLinkAlt,
        onClick: () => onOpenFile(version),
      });
    }
    if (canEditForm) {
      primary.push({
        key: "edit",
        label: "Edit details",
        Icon: FaEdit,
        onClick: onEditForm,
      });
    }
    if (canDeleteVersion) {
      danger.push({
        key: "delete",
        label: "Delete document",
        Icon: FaTrash,
        onClick: onDeleteVersion,
        danger: true,
        disabled: submitting,
      });
    }
    return { primaryItems: primary, dangerItems: danger };
  }, [
    version,
    canEditForm,
    canDeleteVersion,
    submitting,
    onOpenFile,
    onEditForm,
    onDeleteVersion,
  ]);

  const itemCount = primaryItems.length + dangerItems.length;

  const positionMenu = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight || 160;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuHeight + MENU_GAP && rect.top > spaceBelow;
    const width = Math.max(MENU_MIN_WIDTH, menu?.offsetWidth || MENU_MIN_WIDTH);

    setMenuStyle({
      position: "fixed",
      left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
      top: openAbove ? undefined : rect.bottom + MENU_GAP,
      bottom: openAbove ? window.innerHeight - rect.top + MENU_GAP : undefined,
      minWidth: MENU_MIN_WIDTH,
      zIndex: 10100,
      visibility: "visible",
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    positionMenu();
    const frame = requestAnimationFrame(() => positionMenu());
    const onScrollOrResize = () => positionMenu();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, positionMenu, itemCount]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(
    () => () => {
      clearCloseTimer();
    },
    [clearCloseTimer],
  );

  const renderItem = (item) => (
    <button
      key={item.key}
      type="button"
      role="menuitem"
      disabled={item.disabled}
      className={menuItemClass(Boolean(item.danger), Boolean(item.disabled))}
      onClick={() => {
        if (item.disabled) return;
        item.onClick();
        setOpen(false);
      }}
    >
      <span className={iconWrapClass(Boolean(item.danger))}>
        <item.Icon size={12} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1 leading-snug">{item.label}</span>
    </button>
  );

  if (!itemCount) {
    return <span className="text-[11px] text-slate-300">—</span>;
  }

  const dropdown =
    open && typeof document !== "undefined"
      ? createPortal(
          <div className="tailwind-container">
            <div
              ref={menuRef}
              className="relative rounded-xl border border-slate-200/90 bg-white p-1.5 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.18)] ring-1 ring-slate-900/5"
              style={menuStyle}
              role="menu"
              aria-label="Row actions"
              onMouseEnter={clearCloseTimer}
              onMouseLeave={hideMenu}
            >
              <div
                className="absolute -top-2 right-0 h-2 w-10"
                aria-hidden="true"
              />
              <div className="flex flex-col gap-0.5">
                {primaryItems.map(renderItem)}
              </div>
              {dangerItems.length > 0 && primaryItems.length > 0 ? (
                <div
                  className="my-1 border-t border-slate-100"
                  role="separator"
                  aria-hidden="true"
                />
              ) : null}
              {dangerItems.length > 0 ? (
                <div className="flex flex-col gap-0.5">{dangerItems.map(renderItem)}</div>
              ) : null}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className="relative inline-flex justify-end"
      onMouseEnter={showMenu}
      onMouseLeave={hideMenu}
      onFocus={showMenu}
      onBlur={(e) => {
        if (!rootRef.current?.contains(e.relatedTarget) && !menuRef.current?.contains(e.relatedTarget)) {
          hideMenu();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass(open)}
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={open}
        tabIndex={0}
      >
        <FaEllipsisV size={12} aria-hidden="true" />
      </button>
      {dropdown}
    </div>
  );
}
